"use client"

import { useState, useCallback, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion"
import { Calendar, MapPin, Search, X } from "lucide-react"
import LocationSearch from "./location-search"
import FilterDropdown from "./filter-dropdown"
import { Link } from "@tanstack/react-router"
import ProgressBar from "@/components/ui/custom/custom-progress-bar"
import MockDevelopers from '../mockData/InstantMatches.json'
import { ApplicantProfiles } from '../../../../openapi/api/model/applicantProfiles';

interface Skill {
  name: string
  score: number
}

interface Experience {
  role: string
  company: string
  duration: string
  description: string
}

interface Developer {
  id: number
  name: string
  avatar: string
  title: string
  location: string
  availability: string
  skills: Skill[]
  summary: string
  experience: Experience[]
}

interface FilterState {
  startDate: Date | null
  endDate: Date | null
  skills: string[]
  location: string
}



export default function BrowseDevelopers({profiles}:{profiles?:ApplicantProfiles}) {

  const developers=profiles??MockDevelopers

  const [searchTerm, setSearchTerm] = useState<string>("")
  const [filters, setFilters] = useState<FilterState>({
    startDate: null,
    endDate: null,
    skills: [],
    location: "",
  })

  const filteredDevelopers = useMemo((): Developer[] => {
    return developers.filter((dev) => {
      const matchesSearch =
        dev.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dev.skills.some((skill) => skill.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        dev.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        dev.availability.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesSkills =
        filters.skills.length === 0 ||
        filters.skills.every((skill) => dev.skills.some((devSkill) => devSkill.name === skill))

      const matchesLocation = !filters.location || dev.location.toLowerCase().includes(filters.location.toLowerCase())

      return matchesSearch && matchesSkills && matchesLocation
    })
  }, [searchTerm, filters])

  const handleFilterChange = useCallback((newFilters: Partial<FilterState>): void => {
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }))
  }, [])

  const handleClearFilters = useCallback((): void => {
    setFilters({
      startDate: null,
      endDate: null,
      skills: [],
      location: "",
    })
  }, [])

  const handleClearSearch = useCallback((): void => {
    setSearchTerm("")
  }, [])

  const handleLocationSelect = useCallback((location: string): void => {
    setFilters((prevFilters) => ({ ...prevFilters, location }))
  }, [])

  const handleLocationClear = useCallback((): void => {
    setFilters((prevFilters) => ({ ...prevFilters, location: "" }))
  }, [])

  const hasActiveFilters: boolean =
    !!filters.startDate || !!filters.endDate || filters.skills.length > 0 || !!filters.location || !!searchTerm

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">Browse Developers</h1>

      {/* Search and filter section - stacked on mobile, side by side on larger screens */}
      <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-4">
        <div className="relative w-full md:flex-grow">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by name, skills, or availability..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10 w-full h-12"
          />
          {searchTerm && (
            <Button
              variant="ghost"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
              onClick={handleClearSearch}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="w-full md:w-64">
          <LocationSearch
            onLocationSelect={handleLocationSelect}
            selectedLocation={filters.location}
            onClear={handleLocationClear}
          />
        </div>

        <div className="w-full md:w-48">
          <FilterDropdown onFilterChange={handleFilterChange} onClearFilters={handleClearFilters} />
        </div>
      </div>

      {hasActiveFilters && (
        <div className="flex items-center space-x-2 mt-4">
          <span className="text-sm text-muted-foreground">Active filters:</span>
          <Button
            variant="link"
            className="text-sm p-0 h-auto"
            onClick={() => {
              handleClearFilters()
              handleClearSearch()
              handleLocationClear()
            }}
          >
            Clear all
          </Button>
        </div>
      )}

      <div className="space-y-4 md:space-y-6">
        {filteredDevelopers.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-muted-foreground">No developers match your search criteria.</p>
          </div>
        ) : (
          filteredDevelopers.map((dev) => (
            <Card key={dev.id} className="w-full">
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                  <div className="md:w-1/3">
                    <div className="flex items-center space-x-4">
                      <Avatar className="w-14 h-14 md:w-16 md:h-16">
                        <AvatarImage src={dev.avatar} alt={dev.name} />
                        <AvatarFallback>
                          {dev.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h2 className="text-lg md:text-xl font-semibold">{dev.name}</h2>
                        <p className="text-sm text-muted-foreground">{dev.title}</p>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center text-sm text-muted-foreground">
                        <MapPin className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{dev.location}</span>
                      </div>
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="mr-2 h-4 w-4 flex-shrink-0" />
                        <span>{dev.availability}</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Button asChild className="w-full h-10">
                        <Link 
                        to="/allCampaigns/$hiringCampaignId/instantMatch/$applicationId"
                        params={{hiringCampaignId:'67b7a34dae5f8c7d8ea097e8',applicationId:'67c22352caeb8b00b10c8162'}}
                        from="/allCampaigns" >
                          View Profile
                        </Link>
                      </Button>
                    </div>
                    <div className="mt-3">
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem value="skills">
                          <AccordionTrigger className="text-center py-2">View Skill Scores</AccordionTrigger>
                          <AccordionContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {dev.skills.map((skill, index) => (
                                <div key={index} className="space-y-1">
                                  <div className="flex justify-between text-sm">
                                    <span>{skill.name}</span>
                                    <span className="font-medium">{skill.score}%</span>
                                  </div>
                                  <ProgressBar value={skill.score} max={100} className="h-2" color="bg-black" />
                                </div>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </div>
                  </div>
                  <div className="md:w-2/3 space-y-3 md:space-y-4 mt-4 md:mt-0">
                    <p className="text-sm">{dev.summary}</p>
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Top Skills</h3>
                      <div className="flex flex-wrap gap-2">
                        {dev.skills.map((skill, index) => (
                          <Badge key={index} variant="secondary">
                            {skill.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Experience</h3>
                      <div className="space-y-3">
                        {dev.experience.map((exp, index) => (
                          <div key={index} className="text-sm">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                              <div className="font-medium">
                                {exp.role} at {exp.company}
                              </div>
                              <span className="text-muted-foreground text-xs sm:text-sm mt-1 sm:mt-0">
                                {exp.duration}
                              </span>
                            </div>
                            <p className="text-muted-foreground mt-1">{exp.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}

///allCampaigns/67b7a34dae5f8c7d8ea097e8/summary/67c22352caeb8b00b10c8162